import { html } from "htm/preact";
import { useEffect, useState } from "preact/hooks";
import {
  adminDeleteCourse,
  adminDeleteTournament,
  adminListCourses,
  adminListTournaments,
  adminOrganiserLink,
  adminUsage,
  checkAdminKey,
  type AdminCourse,
  type AdminTournament,
  type AdminUsage,
} from "../http.js";
import { health, isNetworkError } from "../http.js";
import { BUILD } from "../buildInfo.js";
import { TERMS } from "../copy.js";
import { navigate } from "../router.js";
import { loadCachedAdminKey, saveCachedAdminKey } from "../adminKey.js";
import { ShareOrCopyButton, organiserUrl } from "../components/ShareButton.js";

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** A measured count, or a dash for a day from before it was measured — never a 0. */
function measured(n: number | null): string {
  return n === null ? "—" : n.toLocaleString();
}

/** "Nov 1" for a `YYYY-MM-DD` UTC day. Short enough that a four-column history still
 * fits a phone without breaking dates across lines. */
function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function Meter({
  label,
  used,
  limit,
  detail,
  allowance,
}: {
  label: string;
  used: string;
  limit: string;
  detail: string;
  allowance: string;
}) {
  const pct = Math.min(100, Number(detail));
  const tone = pct > 80 ? "danger" : pct > 50 ? "warn" : "ok";
  return html`
    <div class="usage-metric">
      <div class="row row-between">
        <strong>${label}</strong>
        <span class="muted">${`${used} / ${limit}`}</span>
      </div>
      <div class="meter"><div class=${`meter-fill ${tone}`} style=${`width:${Math.max(pct, 0.5)}%`}></div></div>
      <span class="muted">${`${pct < 0.1 ? "under 0.1" : pct.toFixed(1)}% of the ${allowance}`}</span>
    </div>
  `;
}

/**
 * What went wrong, in words that fit what actually happened. A fetch that never
 * reached the Worker arrives here with no useful text of its own — the browser says
 * "Load failed" — and on a sign-in screen that reads as "wrong password", which sends
 * you looking for a passphrase that was never the problem.
 */
function describe(err: unknown): string {
  const e = err as Error & { status?: number };
  if (isNetworkError(err)) return e.message;
  if (e.status === 429) return `${e.message} (too many admin requests from this network)`;
  if (e.status && e.status >= 500) {
    return `${e.message} — the server errored on this request. The key was accepted; this is the app's problem, not yours.`;
  }
  return e.message;
}

/**
 * Why the last request didn't work, established rather than guessed.
 *
 * Runs one request that needs no key. Three outcomes, and they point in three
 * completely different directions:
 *
 *  - it fails too → this device can't reach the server at all. Nothing to do with the
 *    passphrase, and everything else in the app will be failing the same way.
 *  - it works, but reports a different build than this page is running → this page is
 *    an old copy kept by the offline cache. A fix can be deployed and merged and still
 *    not be what you are looking at.
 *  - it works and the builds match → the app is current and reachable, so whatever was
 *    refused was refused specifically.
 */
async function diagnose(): Promise<string> {
  try {
    const { build } = await health();
    if (build && build !== "unknown" && BUILD !== "dev" && build !== BUILD) {
      return `This page is an older saved copy of the app (running ${BUILD}; the site is on ${build}). Close the app completely and reopen it — or, if it is on your Home Screen, open divotgolf.club in Safari once — and try again.`;
    }
    return "The app can reach the server, and this page is the current version — so the connection isn't the problem. Something is refusing this particular request: a content blocker, a VPN, or a private-relay setting is the usual cause. Trying another network or another device will show which.";
  } catch {
    return "This device can't reach the server at all right now — even a check that needs no passphrase got no answer. That's the connection, not your key: other parts of the app will be failing the same way. Try another network (Wi-Fi off, or a different one) and reload.";
  }
}

/** A panel that didn't load, saying so where it would have been — with the way back. */
function LoadFailure({ what, message, onRetry, busy }: {
  what: string;
  message: string;
  onRetry: () => void;
  busy: boolean;
}) {
  return html`
    <div class="card card-warn">
      <p class="m-0"><strong>${`Couldn't load ${what}.`}</strong></p>
      <p class="field-help">${message}</p>
      <button class="secondary" disabled=${busy} onClick=${onRetry}>Try again</button>
    </div>
  `;
}

export function Admin() {
  const [key, setKey] = useState(loadCachedAdminKey());
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<AdminTournament[]>([]);
  const [tournamentsError, setTournamentsError] = useState<string | null>(null);
  const [tournamentsTotal, setTournamentsTotal] = useState(0);
  const [usage, setUsage] = useState<AdminUsage | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [confirmingCourse, setConfirmingCourse] = useState<string | null>(null);
  const [confirmingOrganiser, setConfirmingOrganiser] = useState<string | null>(null);
  const [recovered, setRecovered] = useState<Record<string, string>>({});

  /**
   * Each panel loads on its own and keeps its own failure. They used to load together
   * under one `Promise.all`, which meant any one of them failing — a 500, a dropped
   * connection, one sick tournament — threw you back to the passphrase box with the
   * failure written where a rejected key goes. Unlocking is a question about the key
   * and nothing else; what the dashboard can and can't fetch is a separate matter,
   * reported where it happens.
   */
  async function loadTournaments(withKey: string) {
    setTournamentsError(null);
    try {
      const { tournaments: list, total } = await adminListTournaments(withKey);
      setTournaments(list);
      setTournamentsTotal(total);
    } catch (err) {
      setTournamentsError(describe(err));
    }
  }

  async function loadUsage(withKey: string) {
    setUsageError(null);
    try {
      setUsage(await adminUsage(withKey));
    } catch (err) {
      setUsageError(describe(err));
    }
  }

  async function loadCourses(withKey: string) {
    setCoursesError(null);
    try {
      setCourses(await adminListCourses(withKey));
    } catch (err) {
      setCoursesError(describe(err));
    }
  }

  async function load(withKey: string) {
    setBusy(true);
    try {
      await Promise.all([loadTournaments(withKey), loadUsage(withKey), loadCourses(withKey)]);
    } finally {
      setBusy(false);
    }
  }

  /** The whole of signing in: one small call that asks the Worker about the key and
   * nothing else. `silent` is the page-load attempt with a remembered key, where a
   * failure just means showing the form rather than announcing anything. */
  async function unlock(withKey: string, silent = false) {
    setBusy(true);
    if (!silent) {
      setError(null);
      setDiagnosis(null);
    }
    try {
      await checkAdminKey(withKey);
      saveCachedAdminKey(withKey);
      setNotConfigured(false);
      setAuthed(true);
      await load(withKey);
    } catch (err) {
      const e = err as Error & { body?: { notConfigured?: boolean }; status?: number };
      setNotConfigured(Boolean(e.body?.notConfigured));
      if (!silent) setError(describe(err));
      setAuthed(false);
      // A 401 needs no investigation: the key is wrong and it said so. Anything else
      // failed for a reason that isn't the passphrase, so go and find out which.
      if (!silent && e.status !== 401) setDiagnosis(await diagnose());
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (key) void unlock(key, true);
  }, []);

  async function remove(code: string) {
    setBusy(true);
    try {
      await adminDeleteTournament(key, code);
      setConfirming(null);
      await load(key);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Backstop for an organiser whose phone lost its storage — the token is issued once
   * at creation and lives nowhere else, so without this the game is unmanageable. */
  async function recoverOrganiser(code: string) {
    setBusy(true);
    try {
      const { adminToken } = await adminOrganiserLink(key, code);
      setRecovered((prev) => ({ ...prev, [code]: adminToken }));
      setConfirmingOrganiser(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeCourse(id: string) {
    setBusy(true);
    try {
      await adminDeleteCourse(key, id);
      setConfirmingCourse(null);
      await load(key);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!authed) {
    return html`
      <h1>Admin</h1>
      ${notConfigured
        ? html`
            <div class="card card-warn">
              <p>
                The admin area is turned off because no <code>ADMIN_KEY</code> secret is set.
              </p>
              <p class="field-help">
                In the Cloudflare dashboard go to your Worker → Settings → Variables and Secrets →
                Add secret, name it <code>ADMIN_KEY</code>, and set any passphrase you like. It
                stays encrypted and is never sent to the browser. Then reload this page and enter
                the same passphrase below.
              </p>
            </div>
          `
        : html`<p class="field-help">
            ${`Enter the admin passphrase (your ADMIN_KEY Worker secret) to manage ${TERMS.events} and check usage.`}
          </p>`}
      <form
        class="stack"
        onSubmit=${(e: Event) => {
          e.preventDefault();
          unlock(key);
        }}
      >
        <label class="field">
          <span class="field-label">Admin key</span>
          <input
            type="password"
            value=${key}
            onInput=${(e: any) => setKey(e.currentTarget.value)}
            placeholder="Your ADMIN_KEY passphrase"
          />
        </label>
        <button type="submit" disabled=${busy || !key}>Unlock</button>
      </form>
      ${error && !notConfigured && html`<p class="notice notice-error">${error}</p>`}
      ${diagnosis &&
      !notConfigured &&
      html`<div class="card card-warn">
        <p class="m-0"><strong>What that means</strong></p>
        <p class="field-help mb-0">${diagnosis}</p>
      </div>`}
      <p class="field-help">${`App version ${BUILD}`}</p>
    `;
  }

  return html`
    <div class="row row-between">
      <h1 class="m-0">Admin</h1>
      <div class="row">
        <button class="secondary" onClick=${() => navigate("/admin/debug")}>Debug</button>
        <button class="secondary" onClick=${() => load(key)} disabled=${busy}>Refresh</button>
      </div>
    </div>
    ${error && html`<p class="notice notice-error">${error}</p>`}

    <h2>Free plan usage</h2>
    ${usageError &&
    html`<${LoadFailure}
      what="the usage figures"
      message=${usageError}
      onRetry=${() => loadUsage(key)}
      busy=${busy}
    />`}
    ${usage?.truncated &&
    html`<p class="field-help">
      ${`These figures cover the ${usage.countedTournaments} most recent ${TERMS.events} of ${usage.tournamentCount}. One request can only read so many ${TERMS.events} at once on the free plan, so the totals are a floor, not the whole picture.`}
    </p>`}
    ${usage &&
    html`
      <div class="panel mt-0">
        <${Meter}
          label="Durable Object requests today"
          used=${usage.billedRequestsToday.toLocaleString()}
          limit=${usage.limits.doRequestsPerDay.toLocaleString()}
          detail=${String(usage.percentOfRequestLimit)}
          allowance="daily free allowance"
        />
        <${Meter}
          label="Rows read today"
          used=${usage.rowsReadToday.toLocaleString()}
          limit=${usage.limits.rowsReadPerDay.toLocaleString()}
          detail=${String(usage.percentOfReadLimit)}
          allowance="daily free allowance"
        />
        <${Meter}
          label="Rows written today"
          used=${usage.rowsWrittenToday.toLocaleString()}
          limit=${usage.limits.rowsWrittenPerDay.toLocaleString()}
          detail=${String(usage.percentOfWriteLimit)}
          allowance="daily free allowance"
        />
        <${Meter}
          label="Stored data"
          used=${bytes(usage.storageBytes)}
          limit=${bytes(usage.limits.storageBytes)}
          detail=${String(usage.percentOfStorageLimit)}
          allowance="5 GB account storage limit"
        />
        ${/* The other free tier, and the one that will actually run out: fifty course
             lookups a day for the whole deployment, against Cloudflare's hundred
             thousand requests. It sits alongside them because it is the same question
             — what can this app still do today — and a different order of magnitude. */
        usage.courseApi &&
        html`
          <${Meter}
            label="Course database lookups today"
            used=${usage.courseApi.used.toLocaleString()}
            limit=${usage.courseApi.limit.toLocaleString()}
            detail=${String((usage.courseApi.used / usage.courseApi.limit) * 100)}
            allowance="golfcourseapi.com free tier"
          />
        `}
        <p class="field-help">
          ${`${usage.requestsToday.toLocaleString()} HTTP requests and ${usage.wsMessagesToday.toLocaleString()} WebSocket messages today. WebSocket messages bill at 20:1, so the billed figure above counts those as ${Math.ceil(usage.wsMessagesToday / usage.limits.wsMessagesPerRequest).toLocaleString()} requests.`}
        </p>
        <p class="field-help">
          Rows read is the limit a busy day reaches first: every score entered re-reads that
          game's scorecard. Rows are the database's own counts, the ones Cloudflare bills on, and
          include the course library and deleted games. Requests are the app's count, so close
          but not exact. Compute time isn't measurable here; the Cloudflare dashboard has it.
        </p>
        ${usage.courseApi &&
        html`
          <p class="field-help">
            ${`Course lookups are golfcourseapi.com's free-tier allowance — ${usage.courseApi.limit} a day for the whole app, resetting at midnight UTC, with ${usage.courseApi.remaining} left. Only searching for and importing a course spends one; repeats inside a day come from the cache, and everything else in the Course Library is free. That allowance is why adding and editing courses is admin-only.`}
          </p>
        `}
      </div>
      ${usage.history.length > 1 &&
      html`
        <details>
          <summary class="muted">Last ${usage.history.length} days (UTC, when the limits reset)</summary>
          <table class="scorecard-table mt-2">
            <thead>
              <tr><th>Day</th><th>Billed requests</th><th>Rows read</th><th>Rows written</th></tr>
            </thead>
            <tbody>
              ${usage.history.map(
                (h) => html`<tr key=${h.day}>
                  <td class="nowrap">${shortDay(h.day)}</td>
                  <td>${h.billed.toLocaleString()}</td>
                  <td>${measured(h.rowsRead)}</td>
                  <td>${measured(h.rowsWritten)}</td>
                </tr>`,
              )}
            </tbody>
          </table>
        </details>
      `}
    `}

    <div class="panel-head mt-5">
      <h2 class="m-0">${TERMS.Events}</h2>
      <span class="badge">${tournamentsTotal || tournaments.length}</span>
    </div>
    ${tournamentsError &&
    html`<${LoadFailure}
      what=${`the ${TERMS.event} list`}
      message=${tournamentsError}
      onRetry=${() => loadTournaments(key)}
      busy=${busy}
    />`}
    ${tournamentsTotal > tournaments.length &&
    html`<p class="field-help">
      ${`Showing the ${tournaments.length} most recent of ${tournamentsTotal}. Delete what you no longer need — the rest become reachable as the list shortens.`}
    </p>`}
    ${!tournamentsError &&
    tournaments.length === 0 &&
    html`<p class="field-help">${`No ${TERMS.events} yet.`}</p>`}
    <div class="stack">
      ${tournaments.map(
        (t) => html`
          <div class="card" key=${t.code}>
            <div class="row row-between">
              <div>
                <strong>${t.name}</strong> <span class="badge">${t.code}</span>
                ${t.gone && html`<span class="badge">already deleted</span>`}
                ${t.unreachable && html`<span class="badge">unreachable</span>`}
              </div>
              <div class="row">
                <button
                  class="secondary"
                  disabled=${busy || t.gone}
                  onClick=${() => setConfirmingOrganiser(t.code)}
                >
                  ${`${TERMS.Organizer} link`}
                </button>
                <button class="danger" disabled=${busy} onClick=${() => setConfirming(t.code)}>Delete</button>
              </div>
            </div>
            ${confirmingOrganiser === t.code &&
            !recovered[t.code] &&
            html`
              <div class="card card-warn mb-0">
                <p>
                  This reveals a permanent, non-expiring admin credential for
                  <strong>${t.name}</strong> (${t.code}) — anyone who gets it can manage this
                  ${TERMS.event} forever, and it can't be revoked. Only do this for the
                  ${TERMS.organizer} who lost access.
                </p>
                <div class="row">
                  <button class="secondary" disabled=${busy} onClick=${() => recoverOrganiser(t.code)}>
                    Show the link
                  </button>
                  <button class="secondary" onClick=${() => setConfirmingOrganiser(null)}>Cancel</button>
                </div>
              </div>
            `}
            ${recovered[t.code] &&
            html`
              <div class="card mb-0">
                <p class="field-help mt-0">
                  ${`Opening this link restores ${TERMS.organizer} access on whatever device opens it.
                  Send it only to the ${TERMS.organizer}.`}
                </p>
                <${ShareOrCopyButton}
                  label=${`Copy ${TERMS.organizer} link`}
                  url=${organiserUrl(t.code, recovered[t.code]!)}
                  title=${`${TERMS.Organizer} access — ${t.name}`}
                  text=${`${TERMS.Organizer} link for "${t.name}" (${t.code}).`}
                />
              </div>
            `}
            ${t.unreachable &&
            html`<p class="field-help mb-0">
              ${`This ${TERMS.event} didn't answer when asked for its details. It can still be deleted.`}
            </p>`}
            ${!t.gone &&
            !t.unreachable &&
            html`
              <p class="field-help mb-0">
                ${t.status} · ${t.playerCount} players · ${t.roundCount} rounds · ${t.scoreCount} scores
                · ${bytes(t.storageBytes ?? 0)}
                ${t.expiresAt
                  ? html` · expires ${new Date(t.expiresAt).toLocaleDateString()}`
                  : ""}
              </p>
            `}
            ${confirming === t.code &&
            html`
              <div class="card card-danger mb-0">
                <p>
                  Permanently delete <strong>${t.name}</strong> (${t.code}) and all its scores?
                  This can't be undone.
                </p>
                <div class="row">
                  <button class="danger" disabled=${busy} onClick=${() => remove(t.code)}>
                    Yes, delete it
                  </button>
                  <button class="secondary" onClick=${() => setConfirming(null)}>Cancel</button>
                </div>
              </div>
            `}
          </div>
        `,
      )}
    </div>

    <div class="panel-head mt-5">
      <h2 class="m-0">Courses</h2>
      <span class="badge">${courses.length}</span>
    </div>
    ${coursesError &&
    html`<${LoadFailure}
      what="the course list"
      message=${coursesError}
      onRetry=${() => loadCourses(key)}
      busy=${busy}
    />`}
    ${!coursesError && courses.length === 0 && html`<p class="field-help">No courses saved yet.</p>`}
    <div class="stack">
      ${courses.map(
        (c) => html`
          <div class="card" key=${c.id}>
            <div class="row row-between">
              <div>
                <strong>${c.name}</strong>
                ${!c.verified && html`<span class="badge">unverified</span>`}
              </div>
              <button class="danger" disabled=${busy} onClick=${() => setConfirmingCourse(c.id)}>
                Delete
              </button>
            </div>
            <p class="field-help mb-0">
              ${`${c.source === "api" ? "Imported" : "Entered manually"} · ${c.teeSetNames.length} tee ${c.teeSetNames.length === 1 ? "set" : "sets"}: ${c.teeSetNames.join(", ") || "none"}`}
            </p>
            ${c.usedBy.length > 0 &&
            html`<p class="field-help mb-0">
              ${`In use by: ${c.usedBy.map((t) => `${t.name} (${t.code})`).join(", ")}`}
            </p>`}
            ${confirmingCourse === c.id &&
            html`
              <div class="card card-danger mb-0">
                <p>Delete <strong>${c.name}</strong> from the course library?</p>
                ${c.usedBy.length > 0
                  ? html`<p class="text-danger">
                      ${`Careful — ${c.usedBy.length} ${c.usedBy.length === 1 ? TERMS.event : TERMS.events} still use this course. Those rounds will stop scoring until another course is picked.`}
                    </p>`
                  : html`<p class="field-help">${`No ${TERMS.event} is using this course.`}</p>`}
                <div class="row">
                  <button class="danger" disabled=${busy} onClick=${() => removeCourse(c.id)}>
                    Yes, delete it
                  </button>
                  <button class="secondary" onClick=${() => setConfirmingCourse(null)}>Cancel</button>
                </div>
              </div>
            `}
          </div>
        `,
      )}
    </div>
  `;
}
